import { type AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import has from 'lodash-es/has';
import { call, put, takeLatest } from 'redux-saga/effects';

import type Address from '~/types/Address';

import * as addressActions from '~/actions/address.actions';
import { ActionKeys } from '~/actions/constants';
import * as userActions from '~/actions/user.actions';
import { RETURN_ADDRESS_KIND, SENDER_ADDRESS_KIND } from '~/constants/addressBook';
import { FIRST_ORDER_ADDED } from '~/constants/gtm';
import { refetchOrdersCountIfNeeded } from '~/modules/getStarted/utils/ordersCount.util';
import { refetchCurrentOrdersV2Search } from '~/modules/orders/utils/ordersV2.util';
import { getAllOrdersCount } from '~/selectors/orders.selectors';
import { getUserAddress, getUserReturnAddress } from '~/selectors/user.selectors';
import * as addressService from '~/services/address.service';
import { sendGTMEvent } from '~/services/gtm.service';
import * as orderService from '~/services/orders.service';
import * as APIAddress from '~/types/APIAddress';
import { sanitizeAddress, toJSON } from '~/types/Address';
import { newOrder } from '~/types/Order';
import { type ShipmentExtras } from '~/types/OrderBatchEntry';
import { type JSONBatchEntry } from '~/types/OrdersBatch';
import { newRelic } from '~/utils/newRelic/newRelic';

import { selectState } from '..';
import * as manualOrderActions from './../../actions/manual-order.actions';

function* createOrder(action: ReturnType<typeof manualOrderActions.createOrder>) {
  let errorMessage = '';
  let objectId = '';
  const { trackingReference } = action.payload;
  try {
    const conditionals = action.payload.manualOrder.conditionals;
    const addresses = action.payload.manualOrder.addresses;

    const savedAddresses: APIAddress.SavedAPIAddress[] = [];
    const senderAddress = has(addresses, SENDER_ADDRESS_KIND) ? addresses.sender : null;
    const returnAddress = has(addresses, RETURN_ADDRESS_KIND) ? addresses.return : null;

    if (conditionals.saveAsDefaultSenderAddress) {
      errorMessage = 'Unable to save default sender address';
      const senderAPIAddress = APIAddress.newAPIAddress(<APIAddress.default>senderAddress);
      const userReturnAddress: ReturnType<typeof getUserReturnAddress> =
        yield selectState<ReturnType<typeof getUserReturnAddress>>(getUserReturnAddress);

      senderAPIAddress.isDefaultSender = conditionals.saveAsDefaultSenderAddress;
      senderAPIAddress.isDefaultReturn = conditionals.useSenderAddressAsReturn;

      if (
        !senderAPIAddress.isDefaultReturn &&
        userReturnAddress &&
        senderAddress &&
        userReturnAddress.id === senderAddress.id
      )
        senderAPIAddress.isDefaultReturn = userReturnAddress.isDefaultReturn;

      const senderAPIAddressJSON: APIAddress.JSONAPIAddress = APIAddress.toJSON(
        sanitizeAddress(senderAPIAddress),
      )!;

      const response: AxiosResponse<APIAddress.JSONAPIAddress> =
        senderAddress && senderAddress.id !== undefined
          ? yield call(addressService.editApiAddress, senderAddress.id, senderAPIAddressJSON)
          : yield call(addressService.createApiAddress, senderAPIAddressJSON);

      const address = APIAddress.fromJSON(response.data)!;
      savedAddresses.push(address);
    }

    if (conditionals.saveAsDefaultReturnAddress && !conditionals.useSenderAddressAsReturn) {
      errorMessage = 'Unable to save default return address';
      const returnAPIAddress = APIAddress.newAPIAddress(<APIAddress.default>returnAddress);

      if (conditionals.saveAsDefaultSenderAddress) {
        if (senderAddress && returnAddress && returnAddress.id === senderAddress.id)
          returnAPIAddress.isDefaultSender = senderAddress.isDefaultSender;
      } else {
        const userAddress: ReturnType<typeof getUserAddress> =
          yield selectState<ReturnType<typeof getUserAddress>>(getUserAddress);
        if (returnAddress && returnAddress.id === userAddress?.id)
          returnAPIAddress.isDefaultSender = userAddress?.isDefaultSender;
      }

      returnAPIAddress.isDefaultReturn = conditionals.saveAsDefaultReturnAddress;
      const returnAPIAddressJSON: APIAddress.JSONAPIAddress = APIAddress.toJSON(
        sanitizeAddress(returnAPIAddress),
      )!;
      const response: AxiosResponse<APIAddress.JSONAPIAddress> =
        returnAddress && returnAddress.id !== undefined
          ? yield call(addressService.editApiAddress, returnAddress.id, returnAPIAddressJSON)
          : yield call(addressService.createApiAddress, returnAPIAddressJSON);

      const address = APIAddress.fromJSON(response.data)!;
      savedAddresses.push(address);
    }

    const savedAddressesCount = savedAddresses.length;
    for (let i = 0; i < savedAddressesCount; i += 1) {
      const address = savedAddresses[i];
      const { isDefaultReturn, isDefaultSender } = address;
      if (isDefaultSender) yield put(userActions.updateUserDefaultAddress({ address }));

      if (isDefaultReturn) yield put(userActions.updateUserDefaultReturnAddress({ address }));

      if (
        (senderAddress && address.id === senderAddress.id) ||
        (returnAddress && address.id === returnAddress.id)
      ) {
        yield put(
          addressActions.editAPIAddressSuccess({
            address,
            id: address.id,
          }),
        );
      } else {
        yield put(addressActions.createAPIAddressSuccess({ address, trackingReference }));
      }
    }

    errorMessage = 'Unable to create order';
    const fromAddress = toJSON(action.payload.manualOrder.addresses.sender as Address);
    const toAddress = toJSON(action.payload.manualOrder.addresses.recipient as Address);

    const order = newOrder(fromAddress, toAddress);
    const savedOrder: AxiosResponse = yield call(orderService.createOrder, order);

    errorMessage = 'Unable to set return address of new order';
    const object_id = savedOrder.data.object_id;

    // Use this get/create batch entry since other endpoint should get deprecated in favor of this one
    const batchEntryResponse: AxiosResponse<JSONBatchEntry[]> = yield call(
      orderService.getOrCloneBatchEntriesForOrders,
      [object_id],
      [],
    );

    const batchEntry = batchEntryResponse.data;
    const batchEntryId = batchEntry[0].id;

    const returnAddressToUse = conditionals.useSenderAddressAsReturn
      ? addresses.sender
      : addresses.return;
    const sanitizedReturnAddress = sanitizeAddress(<APIAddress.default>returnAddressToUse);

    yield call(orderService.updateBatchEntryAddress, sanitizedReturnAddress, RETURN_ADDRESS_KIND, [
      batchEntryId,
    ]);

    if (conditionals.bypassAddressValidation) {
      try {
        yield call(orderService.updateBatchEntryExtras, [batchEntryId], {
          isBypassAddressValidation: true,
        } as ShipmentExtras);
      } catch (error) {
        newRelic.noticeError(
          'Failed to update isBypassAddressValidation from manual label creation',
          {
            error,
            response: error.response?.data,
          },
        );
      }
    }

    objectId = object_id!;
    yield put(manualOrderActions.createOrderSuccess(objectId));
    yield put(manualOrderActions.manualOrderReset());
    yield put(push(`/orders/${objectId}`));
    // send tracking for first order
    const userOrderCount: ReturnType<typeof getAllOrdersCount> =
      yield selectState<ReturnType<typeof getAllOrdersCount>>(getAllOrdersCount);
    if (userOrderCount === 0) {
      yield call(sendGTMEvent, {
        eventName: FIRST_ORDER_ADDED,
      });
    }
  } catch (error) {
    if (objectId.length > 0) {
      yield put(manualOrderActions.manualOrderReset());
      yield put(push(`/orders/${objectId}`));
    } else {
      yield put(manualOrderActions.createOrderError(errorMessage, error as Error));
      if (errorMessage === 'Unable to save default sender address') {
        newRelic.noticeError('Failed to save default sender address', {
          error,
          response: error.response?.data,
        });
      }
    }
  }
}

const createOrderSuccess = () => {
  refetchOrdersCountIfNeeded();
  refetchCurrentOrdersV2Search();
};

export default [
  takeLatest(ActionKeys.MANUALORDER_SAVE_ORDER, createOrder),
  takeLatest(ActionKeys.MANUALORDER_SAVE_ORDER_SUCCESS, createOrderSuccess),
];
